import type { StateCreator } from "zustand";
import type { RealtimeChannel } from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";
import { showToast } from "../../lib/toast";
import type { ITaskDependency } from "../../types";
import { ZTaskDependency } from "../../types/zod-schemas";
import type { IStoreState } from "../useStore";

export interface DependencySlice {
  dependencies: ITaskDependency[];
  dependencyChannel: RealtimeChannel | null;

  fetchDependencies: () => Promise<void>;
  addPrerequisite: (prerequisiteUuid: string, dependentUuid: string) => Promise<boolean>;
  removeDependency: (dependencyUuid: string) => Promise<void>;
  /**
   * Soft-deletes all active dependency rows that touch the given task UUIDs.
   * Called when a task (or chain of tasks) is completed to "break" the chain
   * from the user's perspective while preserving historical data.
   */
  breakDependenciesForTasks: (taskUuids: string[]) => Promise<void>;
  subscribeToDependencyRealtime: () => void;
  unsubscribeFromDependencyRealtime: () => void;
}

/**
 * Walk the prerequisite graph upward from a given task.
 * Returns true if `target` is reachable as a (transitive) prerequisite of `start`.
 */
function isAncestor(
  dependencies: ITaskDependency[],
  start: string,
  target: string
): boolean {
  if (start === target) return true;
  const stack: string[] = [start];
  const seen = new Set<string>();
  while (stack.length > 0) {
    const cur = stack.pop()!;
    if (seen.has(cur)) continue;
    seen.add(cur);
    for (const d of dependencies) {
      if (d.dependent_uuid === cur) {
        if (d.prerequisite_uuid === target) return true;
        stack.push(d.prerequisite_uuid);
      }
    }
  }
  return false;
}

export const createDependencySlice: StateCreator<
  IStoreState,
  [["zustand/devtools", never]],
  [],
  DependencySlice
> = (set, get) => ({
  dependencies: [],
  dependencyChannel: null,

  fetchDependencies: async () => {
    const { user } = get();
    if (!user) return;

    const { data, error } = await supabase
      .from("task_dependencies")
      .select("*")
      .eq("user_uuid", user.id)
      .is("deleted_at", null);

    if (!error && data) {
      set({ dependencies: data });
    }
  },

  addPrerequisite: async (prerequisiteUuid: string, dependentUuid: string) => {
    const { user, dependencies } = get();
    if (!user) return false;

    if (prerequisiteUuid === dependentUuid) {
      showToast("A task cannot be a prerequisite of itself");
      return false;
    }

    // Cycle check: the new prerequisite must not already be a (transitive) dependent of the dependent.
    // i.e. if the new prerequisite already depends on `dependentUuid` somewhere upstream, we'd make a cycle.
    if (isAncestor(dependencies, prerequisiteUuid, dependentUuid)) {
      showToast("That would create a dependency cycle");
      return false;
    }

    // Duplicate check (active rows)
    const exists = dependencies.some(
      (d) =>
        d.prerequisite_uuid === prerequisiteUuid &&
        d.dependent_uuid === dependentUuid
    );
    if (exists) return true;

    const { data, error } = await supabase
      .from("task_dependencies")
      .insert({
        user_uuid: user.id,
        prerequisite_uuid: prerequisiteUuid,
        dependent_uuid: dependentUuid,
      })
      .select()
      .single();

    if (error || !data) {
      console.error("Failed to add prerequisite:", error);
      showToast("Failed to add prerequisite");
      return false;
    }

    set({ dependencies: [...get().dependencies, data] });
    return true;
  },

  removeDependency: async (dependencyUuid: string) => {
    const { error } = await supabase
      .from("task_dependencies")
      .update({ deleted_at: new Date().toISOString() })
      .eq("uuid", dependencyUuid);

    if (error) {
      console.error("Failed to remove dependency:", error);
      showToast("Failed to remove dependency");
      return;
    }

    set({
      dependencies: get().dependencies.filter((d) => d.uuid !== dependencyUuid),
    });
  },

  breakDependenciesForTasks: async (taskUuids: string[]) => {
    if (taskUuids.length === 0) return;
    const { dependencies } = get();

    const toBreak = dependencies.filter(
      (d) =>
        taskUuids.includes(d.prerequisite_uuid) ||
        taskUuids.includes(d.dependent_uuid)
    );
    if (toBreak.length === 0) return;

    const ids = toBreak.map((d) => d.uuid);
    const now = new Date().toISOString();

    const { error } = await supabase
      .from("task_dependencies")
      .update({ deleted_at: now })
      .in("uuid", ids);

    if (error) {
      console.error("Failed to break dependency chain:", error);
      showToast("Failed to update dependencies");
      return;
    }

    set({
      dependencies: get().dependencies.filter((d) => !ids.includes(d.uuid)),
    });
  },

  subscribeToDependencyRealtime: () => {
    const { user, dependencyChannel } = get();
    if (!user || dependencyChannel) return;

    const channel = supabase
      .channel("task-dependencies-changes")
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "task_dependencies",
          filter: `user_uuid=eq.${user.id}`,
        },
        (payload) => {
          const { eventType } = payload;

          if (eventType === "INSERT") {
            try {
              const dep = ZTaskDependency.parse(payload.new);
              if (dep.deleted_at) return;
              const exists = get().dependencies.some((d) => d.uuid === dep.uuid);
              if (!exists) {
                set({ dependencies: [...get().dependencies, dep] });
              }
            } catch (e) {
              console.error("Invalid realtime dependency payload:", e);
            }
          }

          if (eventType === "UPDATE") {
            try {
              const dep = ZTaskDependency.parse(payload.new);
              if (dep.deleted_at) {
                set({
                  dependencies: get().dependencies.filter(
                    (d) => d.uuid !== dep.uuid
                  ),
                });
              } else {
                set({
                  dependencies: get().dependencies.map((d) =>
                    d.uuid === dep.uuid ? dep : d
                  ),
                });
              }
            } catch (e) {
              console.error("Invalid realtime dependency payload:", e);
            }
          }

          if (eventType === "DELETE") {
            const old = payload.old as { uuid?: string };
            if (old.uuid) {
              set({
                dependencies: get().dependencies.filter(
                  (d) => d.uuid !== old.uuid
                ),
              });
            }
          }
        }
      )
      .subscribe();

    set({ dependencyChannel: channel });
  },

  unsubscribeFromDependencyRealtime: () => {
    const { dependencyChannel } = get();
    if (dependencyChannel) {
      supabase.removeChannel(dependencyChannel);
      set({ dependencyChannel: null });
    }
  },
});
